import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

const CATEGORIES = ["Rent", "Salaries", "Utilities", "Marketing", "Travel", "Software", "Other Expenses"];

export default function Expenses() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0,10),
    category: "Rent", amount: 0, method: "bank", reference: "", notes: "",
  });

  const load = async () => setRows((await api.get("/expenses")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense recorded");
      setShow(false);
      setForm({ date: new Date().toISOString().slice(0,10), category: "Rent", amount: 0, method: "bank", reference: "", notes: "" });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const total = rows.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="space-y-5 lb-fade" data-testid="expenses-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Operating</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Expenses</h1>
        </div>
        <button className="lb-btn" onClick={() => setShow((s) => !s)} data-testid="add-expense-btn">
          <Plus size={14} /> Record Expense
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="lb-kpi"><div className="lb-kpi-label">Total Expenses</div><div className="lb-kpi-value mono">{fmtINR(total)}</div></div>
        <div className="lb-kpi"><div className="lb-kpi-label">Entries</div><div className="lb-kpi-value mono">{rows.length}</div></div>
        <div className="lb-kpi"><div className="lb-kpi-label">Categories</div><div className="lb-kpi-value mono">{new Set(rows.map(r => r.category)).size}</div></div>
      </div>

      {show && (
        <form onSubmit={submit} className="lb-card p-6 grid grid-cols-4 gap-4">
          <div>
            <label className="lb-label">Date</label>
            <input type="date" required className="lb-input" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="lb-label">Category</label>
            <select required className="lb-select" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ex-category">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="lb-label">Amount</label>
            <input type="number" step="0.01" required className="lb-input font-mono text-right"
                   value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ex-amount" />
          </div>
          <div>
            <label className="lb-label">Method</label>
            <select className="lb-select" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
              <option value="bank">Bank</option><option value="cash">Cash</option>
            </select>
          </div>
          <div>
            <label className="lb-label">Reference</label>
            <input className="lb-input font-mono" value={form.reference}
                   onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </div>
          <div className="col-span-3">
            <label className="lb-label">Notes</label>
            <input className="lb-input" value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="col-span-4 flex gap-2">
            <button className="lb-btn" data-testid="ex-submit">Save Expense</button>
            <button type="button" className="lb-btn lb-btn-ghost" onClick={() => setShow(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="lb-card">
        <table className="lb-table">
          <thead><tr><th>Date</th><th>Category</th><th className="text-right">Amount</th><th>Method</th><th>Reference</th><th>Notes</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="font-mono text-sm">{fmtDate(r.date)}</td>
                <td><span className="lb-badge">{r.category}</span></td>
                <td className="amt">{fmtINR(r.amount)}</td>
                <td className="text-sm">{r.method}</td>
                <td className="font-mono text-xs">{r.reference}</td>
                <td className="text-sm text-neutral-600">{r.notes}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="text-center py-10 text-neutral-500">No expenses</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
